import React from 'react'
import { Link } from 'react-router-dom'
import { TrendingUp, Mail, ArrowUpRight, ShieldCheck } from 'lucide-react'
import { cn } from '../utils/cn'

const OWNER_EMAIL = 'godjillar@gmail.com'
const YEAR = new Date().getFullYear()

const COLUMNS = [
  {
    title: 'Product',
    links: [
      { label: 'Features',           to: '/features' },
      { label: 'Live streaming',     to: '/features#streaming' },
      { label: 'Portfolio & health', to: '/features#portfolio' },
      { label: 'Rebalance engine',   to: '/features#rebalance' },
      { label: 'Fund intelligence',  to: '/features#funds' },
      { label: 'Knowledge workspace', to: '/features#notes' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About',   to: '/about' },
      { label: 'FAQ',     to: '/faq' },
      { label: 'Contact', to: '/contact' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy', to: '/privacy' },
      { label: 'Terms',   to: '/terms' },
    ],
  },
]

function BrandMark({ className }) {
  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-accent-500 to-accent-700 shadow-glow-accent flex items-center justify-center">
        <TrendingUp className="w-5 h-5 text-white" strokeWidth={2.5} />
      </div>
      <div className="leading-none">
        <div className="font-display font-semibold text-ink-900 text-[15px] tracking-tight">serenDPT</div>
        <div className="text-[10px] text-ink-400 mt-0.5 tracking-wide uppercase">Trading Intelligence</div>
      </div>
    </div>
  )
}

const FootLink = ({ to, children }) => {
  const isHash = to.includes('#')
  const cls = 'text-sm text-ink-500 hover:text-accent-700 transition-colors'
  return isHash
    ? <a href={to} className={cls}>{children}</a>
    : <Link to={to} className={cls}>{children}</Link>
}

/**
 * Footer — world-class, theme-matched.
 *   variant="full"    → full multi-column footer (public / marketing pages)
 *   variant="compact" → slim single-row footer (inside the authenticated app)
 */
export default function Footer({ variant = 'full', className }) {
  if (variant === 'compact') {
    return (
      <footer className={cn('border-t border-ink-100 bg-white/60', className)}>
        <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center gap-3 sm:gap-5 text-xs text-ink-400">
          <span className="flex items-center gap-1.5 font-semibold text-ink-500">
            <span className="w-5 h-5 rounded-md bg-gradient-to-br from-accent-500 to-accent-700 flex items-center justify-center">
              <TrendingUp className="w-3 h-3 text-white" strokeWidth={2.5} />
            </span>
            serenDPT
          </span>
          <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <FootLink to="/about">About</FootLink>
            <FootLink to="/features">Features</FootLink>
            <FootLink to="/contact">Contact</FootLink>
            <FootLink to="/privacy">Privacy</FootLink>
            <FootLink to="/terms">Terms</FootLink>
          </nav>
          <span className="sm:ml-auto text-center">© {YEAR} serenDPT · Personal use · Not investment advice</span>
        </div>
      </footer>
    )
  }

  return (
    <footer className={cn('border-t border-ink-100 bg-gradient-to-b from-white to-ink-25', className)}>
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-8">
        {/* Top — brand + columns */}
        <div className="py-12 grid grid-cols-2 md:grid-cols-12 gap-8">
          {/* Brand block */}
          <div className="col-span-2 md:col-span-4">
            <BrandMark />
            <p className="mt-4 text-sm text-ink-500 leading-6 max-w-xs">
              A personal trading-intelligence platform — live market data, unified portfolio
              analytics, fund research and a private knowledge workspace, built for one
              disciplined investor.
            </p>
            <a
              href={`mailto:${OWNER_EMAIL}`}
              className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-ink-600 hover:text-accent-700 transition-colors"
            >
              <Mail className="w-4 h-4" />
              {OWNER_EMAIL}
            </a>
          </div>

          {/* Spacer */}
          <div className="hidden md:block md:col-span-1" />

          {/* Link columns */}
          {COLUMNS.map((col) => (
            <div key={col.title} className="md:col-span-2">
              <div className="kicker mb-3">{col.title}</div>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}><FootLink to={l.to}>{l.label}</FootLink></li>
                ))}
              </ul>
            </div>
          ))}

          {/* Get started */}
          <div className="md:col-span-1">
            <div className="kicker mb-3">Access</div>
            <ul className="space-y-2.5">
              <li><FootLink to="/login">Sign in</FootLink></li>
              <li><FootLink to="/register">Create account</FootLink></li>
            </ul>
          </div>
        </div>

        {/* Disclaimer strip */}
        <div className="py-4 border-t border-ink-100 flex items-start gap-2.5 text-xs text-ink-400 leading-5">
          <ShieldCheck className="w-4 h-4 mt-px shrink-0 text-ink-300" />
          <p>
            serenDPT is a personal portfolio &amp; research tool. It does <span className="font-medium text-ink-500">not</span> provide
            investment advice, and nothing here is a recommendation to buy or sell any security.
            Markets carry risk; you are responsible for your own decisions. Order placement, where
            available, routes through your own connected broker accounts.
          </p>
        </div>

        {/* Bottom bar */}
        <div className="py-6 border-t border-ink-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-ink-400">
          <span>© {YEAR} serenDPT. Built for personal use.</span>
          <span className="flex items-center gap-4">
            <FootLink to="/privacy">Privacy</FootLink>
            <FootLink to="/terms">Terms</FootLink>
            <a
              href={`mailto:${OWNER_EMAIL}`}
              className="inline-flex items-center gap-1 hover:text-accent-700 transition-colors"
            >
              Contact <ArrowUpRight className="w-3 h-3" />
            </a>
          </span>
        </div>
      </div>
    </footer>
  )
}

export { BrandMark, OWNER_EMAIL }
